import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faGear, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FeatureFlag } from "../../../models/feature-flag.model";
import { FeatureFlagService } from "../../../services/feature-flag.service";
import { FeatureFlagsDataSource } from "../../../utility/feature-flag-datasource";
import { ToastService } from "../../../services/toast.service";
import {
  CdkCell,
  CdkCellDef,
  CdkColumnDef,
  CdkHeaderCell,
  CdkHeaderCellDef,
  CdkHeaderRow,
  CdkHeaderRowDef,
  CdkRow,
  CdkRowDef,
  CdkTable
} from "@angular/cdk/table";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { NgClass } from "@angular/common";
import { StateManagementService } from "../../../services/state-management.service";

@Component({
  selector: "app-feature-flags",
  standalone: true,
  imports: [
    CdkTable,
    FaIconComponent,
    CdkColumnDef,
    CdkHeaderCell,
    CdkHeaderCellDef,
    CdkCellDef,
    CdkCell,
    ReactiveFormsModule,
    CdkRow,
    CdkHeaderRowDef,
    CdkHeaderRow,
    CdkRowDef,
    NgClass
  ],
  templateUrl: "./feature-flags.component.html",
  styleUrl: "./feature-flags.component.scss"
})
export class FeatureFlagsComponent implements OnInit, OnDestroy {
  protected readonly faGear: IconDefinition = faGear;
  protected readonly faPlus: IconDefinition = faPlus;
  protected newFeatureFlagForm!: FormGroup;
  protected dataSource: FeatureFlagsDataSource;
  protected displayedColumns: string[] = ["name", "description", "enabled", "actions"];
  protected editForms: Map<number, FormGroup> = new Map();
  protected showNewFeatureFlagForm: boolean = false;

  constructor(
    private featureFlagService: FeatureFlagService,
    private stateManagementService: StateManagementService,
    private toastService: ToastService,
    private formBuilder: FormBuilder
  ) {
    this.dataSource = new FeatureFlagsDataSource(this.featureFlagService, this.toastService);
  }

  ngOnInit(): void {
    this.stateManagementService.setPageTitle("Feature Flags | Momentum Edge");
    this.stateManagementService.pageHeading.set("Feature Flags");
    this.newFeatureFlagForm = this.formBuilder.group({
      name: ["", [Validators.required]],
      description: ["", [Validators.required]],
      enabled: [false]
    });
    this.dataSource.loadFeatureFlags();
  }

  protected getNewFormControl(controlName: string): FormControl {
    return this.newFeatureFlagForm.get(controlName) as FormControl;
  }

  private isControlInvalid(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  protected isNewFormControlInvalid(controlName: string): boolean {
    const control: FormControl<any> = this.getNewFormControl(controlName);
    return this.isControlInvalid(control);
  }

  protected toggleNewFeatureFlagForm(): void {
    this.showNewFeatureFlagForm = !this.showNewFeatureFlagForm;
    if (this.showNewFeatureFlagForm) {
      this.newFeatureFlagForm.reset({
        name: "",
        description: "",
        enabled: false
      });
    }
  }

  private initializeEditForm(featureFlag: FeatureFlag): void {
    const editForm = this.formBuilder.group({
      name: [featureFlag.name, [Validators.required]],
      description: [featureFlag.description, [Validators.required]],
      enabled: [featureFlag.enabled]
    });

    if (featureFlag.id) {
      this.editForms.set(featureFlag.id, editForm);
    }
  }

  protected getEditFormControl(featureFlagId: number, controlName: string): FormControl | null {
    const editFormGroup: FormGroup | undefined = this.editForms.get(featureFlagId);
    return editFormGroup ? (editFormGroup.get(controlName) as FormControl) : null;
  }

  protected toggleEdit(featureFlag: FeatureFlag): void {
    featureFlag.editMode = !featureFlag.editMode;
    this.showNewFeatureFlagForm = false;
    if (featureFlag.editMode) {
      this.initializeEditForm(featureFlag);
    }
  }

  protected addNewFeatureFlag(): void {
    if (this.newFeatureFlagForm.valid) {
      const newFeatureFlag: FeatureFlag = this.newFeatureFlagForm.value;
      this.dataSource.addFeatureFlag(newFeatureFlag);
      this.toggleNewFeatureFlagForm();
    } else {
      // Mark all fields as touched to trigger validation messages
      Object.keys(this.newFeatureFlagForm.controls).forEach((controlName: string): void => {
        this.newFeatureFlagForm.get(controlName)?.markAsTouched();
      });
    }
  }

  protected saveChanges(featureFlag: FeatureFlag): void {
    if (featureFlag.id) {
      const editFormGroup: FormGroup | undefined = this.editForms.get(featureFlag.id);
      if (editFormGroup && editFormGroup.valid) {
        const updatedFlag: FeatureFlag = {
          ...featureFlag,
          name: editFormGroup.value.name,
          description: editFormGroup.value.description,
          enabled: editFormGroup.value.enabled
        };

        this.dataSource.updateFeatureFlag(updatedFlag);
        featureFlag.editMode = false;
      }
    }
  }

  protected cancelEdit(featureFlag: FeatureFlag): void {
    featureFlag.editMode = false;
    this.initializeEditForm(featureFlag);
  }

  protected toggleEnabled(featureFlag: FeatureFlag): void {
    const updatedFlag = { ...featureFlag, enabled: !featureFlag.enabled };
    this.dataSource.updateFeatureFlag(updatedFlag);
  }

  ngOnDestroy(): void {
    this.stateManagementService.resetHeadingAndTitle();
    this.dataSource.disconnect();
  }
}
